import { useEffect } from "react";
import { Shell, type NavGruppe } from "../ui/Shell";
import { Link } from "react-router";
import { Notice } from "../ui";
import { useIch } from "../api/ich";
import { useLaden } from "./api";
import { formatDatumZeit } from "@shared/zeit";

/** Navigation der Verwaltung – nur Einträge, die es in dieser Etappe gibt (keine toten Links). */
export function LadenShell() {
  const ich = useIch();
  const laden = useLaden();
  const rechte = ich.data?.rechte ?? [];
  const gruppen: NavGruppe[] = [
    { titel: "Heute", eintraege: [{ to: "/laden", label: "Übersicht", icon: "layout-dashboard", end: true }, { to: "/laden/verkaeufe", label: "Verkäufe und Abgleich", icon: "receipt" }, { to: "/kasse", label: "Zur Kasse", icon: "scan-line" }] },
    { titel: "Regale", eintraege: [{ to: "/laden/regale", label: "Regale", icon: "library" }, { to: "/laden/belegung", label: "Belegung", icon: "calendar-days" }] },
    {
      titel: "Mieter",
      eintraege: [
        { to: "/laden/mieter", label: "Mieter", icon: "users" },
        { to: "/laden/artikel", label: "Artikel und Etiketten", icon: "tag" },
        { to: "/laden/termine", label: "Ein- und Auszüge", icon: "calendar-days" },
        { to: "/laden/warteliste", label: "Warteliste", icon: "clock" },
        { to: "/laden/neuigkeiten", label: "Neuigkeiten", icon: "bell" },
        { to: "/laden/events", label: "Events", icon: "sparkles" },
        { to: "/laden/feedback", label: "Feedback", icon: "mail" },
        { to: "/laden/newsletter", label: "Kunden und Newsletter", icon: "store" },
      ],
    },
  ];
  const filialen = (ich.data?.laeden ?? []).filter((l) => l.rolle === "inhaber");
  if (filialen.length > 1) gruppen.push({ titel: "Filialen", eintraege: [{ to: "/laden/filialen", label: "Alle Läden", icon: "library" }] });
  gruppen.push({ titel: "Personal", eintraege: [{ to: "/laden/aufgaben", label: "Aufgaben", icon: "check" }, { to: "/laden/schichtplan", label: "Schichtplan", icon: "calendar-days" }] });
  gruppen.push({ titel: "Geld", eintraege: [{ to: "/laden/abrechnungen", label: "Abrechnungen", icon: "file-text" }, { to: "/laden/forderungen", label: "Forderungen", icon: "euro" }, { to: "/laden/kassenbuch", label: "Kassenbuch", icon: "banknote" }, { to: "/laden/sepa", label: "SEPA", icon: "credit-card" }, { to: "/laden/auswertungen", label: "Auswertungen", icon: "list" }] });
  if (rechte.includes("einstellungen")) {
    gruppen.push({
      titel: "System",
      eintraege: [
        { to: "/laden/einstellungen", label: "Einstellungen", icon: "settings" },
        { to: "/laden/import", label: "Import", icon: "archive" },
        { to: "/laden/protokoll", label: "Änderungsprotokoll", icon: "shield-check" },
      ],
    });
  }
  const name = laden.data?.name ?? "Laden";
  const akzent = laden.data?.einstellungen.branding.akzent ?? null;
  useEffect(() => {
    document.documentElement.style.setProperty("--accent", akzent ?? "");
    return () => {
      document.documentElement.style.removeProperty("--accent");
    };
  }, [akzent]);
  gruppen.push({ eintraege: [{ to: "/laden/konto", label: "Mein Konto", icon: "user" }] });
  const totpBanner =
    ich.data?.totpPflicht && !ich.data.totpAktiv ? (
      <Notice tone="attention" title="Zwei-Faktor-Anmeldung einrichten" action={<Link to="/laden/konto" className="rg-btn rg-btn--primary">Jetzt einrichten</Link>}>
        Für Inhaber und alle mit Zugriff auf Geld, Einstellungen oder Personal ist der zweite Faktor Pflicht. Bis zur Einrichtung sind diese Bereiche schreibgeschützt.
      </Notice>
    ) : null;
  const abo = laden.data?.abo;
  const aboBanner = abo?.gesperrt ? (
    <Notice tone="attention" title="Neue Buchungen sind gesperrt" action={rechte.includes("einstellungen") ? <Link to="/laden/einstellungen/abo" className="rg-btn rg-btn--primary">Zum Abo</Link> : undefined}>
      {abo.grund} Lesen, Kassieren und Abrechnen bleiben möglich.
    </Notice>
  ) : abo?.aboStatus === "test" && abo.testTageRest !== null && abo.testTageRest <= 5 ? (
    <Notice tone="info" title={abo.testTageRest === 0 ? "Die Testphase endet heute" : `Die Testphase endet in ${abo.testTageRest} Tagen`} action={rechte.includes("einstellungen") ? <Link to="/laden/einstellungen/abo" className="rg-btn rg-btn--secondary">Abo ansehen</Link> : undefined}>
      Danach sind neue Buchungen gesperrt, bis das Abo läuft. Alles andere bleibt wie es ist.
    </Notice>
  ) : null;
  const supportBanner = ich.data?.support ? (
    <Notice tone="attention" icon="shield-check" title="Support-Zugang des Anbieters">
      Diese Sitzung ist bis {formatDatumZeit(ich.data.support.bis)} gültig und wird im Änderungsprotokoll des Ladens vermerkt. Auszahlungen, IBAN-Klartext und Exporte sind ausgeschlossen.
    </Notice>
  ) : null;
  const hinweis = totpBanner || aboBanner || supportBanner ? (
    <>
      {supportBanner}
      {totpBanner}
      {aboBanner}
    </>
  ) : null;
  return <Shell brand={name} kontext={`Verwaltung · ${ich.data?.benutzer?.name || ich.data?.benutzer?.email || ""}`} gruppen={gruppen} logoUrl={laden.data?.einstellungen.branding.logoKey ? `/api/public/laden/${laden.data.slug}/logo` : null} hinweis={hinweis} />;
}
